/**
 * Aceleres: portão único de validação das "Preferências de Notificação".
 *
 * Todo alerta disparado pelo dashboard (notificação de desktop do navegador ou
 * som) passa por aqui antes de aparecer para o usuário. A regra é simples:
 * enquanto não houver confirmação de que a categoria, o canal e a permissão do
 * navegador estão habilitados para ESTE usuário, nada é exibido.
 *
 * As preferências são lidas direto da store no momento do disparo — nunca
 * copiadas para variáveis locais — para que qualquer alteração feita em
 * /configuracoes/perfil valha imediatamente, sem recarregar a página.
 */

// Espelha NOTIFICATION_TYPES (settings/profile/constants.js) e os flags
// `push_*` persistidos em notification_settings.
export const PUSH_NOTIFICATION_FLAGS = {
  CONVERSATION_CREATION: 'conversation_creation',
  CONVERSATION_ASSIGNMENT: 'conversation_assignment',
  CONVERSATION_MENTION: 'conversation_mention',
  ASSIGNED_CONVERSATION_NEW_MESSAGE: 'assigned_conversation_new_message',
  PARTICIPATING_CONVERSATION_NEW_MESSAGE:
    'participating_conversation_new_message',
};

export const BROWSER_PUSH_STATE = {
  UNKNOWN: 'unknown',
  SUBSCRIBED: 'subscribed',
  UNSUBSCRIBED: 'unsubscribed',
  UNSUPPORTED: 'unsupported',
};

// Estado da chave "Ative notificações push em seu navegador" do perfil. O
// navegador mantém `Notification.permission === 'granted'` mesmo depois que o
// usuário desliga a chave (o que apenas cancela a subscription), então a
// permissão sozinha não representa a preferência.
let browserPushState = BROWSER_PUSH_STATE.UNKNOWN;

export const getBrowserPushState = () => browserPushState;

export const setBrowserPushSubscribed = isSubscribed => {
  browserPushState = isSubscribed
    ? BROWSER_PUSH_STATE.SUBSCRIBED
    : BROWSER_PUSH_STATE.UNSUBSCRIBED;
};

export const resetBrowserPushState = () => {
  browserPushState = BROWSER_PUSH_STATE.UNKNOWN;
};

/**
 * Consulta a subscription real do service worker e guarda o resultado.
 * Chamado no boot da conta e sempre que a chave do perfil é alternada.
 */
export const refreshBrowserPushState = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    browserPushState = BROWSER_PUSH_STATE.UNSUPPORTED;
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => registration.pushManager.getSubscription())
    .then(subscription => setBrowserPushSubscribed(!!subscription))
    .catch(() => {
      // Sem resposta confiável do service worker preservamos o estado atual;
      // as demais validações (categoria, canal e permissão) continuam valendo.
    });
};

export const hasBrowserNotificationPermission = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  window.Notification.permission === 'granted';

// Só bloqueia quando temos certeza de que a chave está desligada. `unknown` e
// `unsupported` deixam a decisão para permissão + categoria + canal.
export const isBrowserPushDisabledByUser = () =>
  browserPushState === BROWSER_PUSH_STATE.UNSUBSCRIBED;

const getPushFlags = store =>
  store?.getters?.['userNotificationSettings/getSelectedPushFlags'];

/**
 * Uma categoria só libera notificação quando o flag `push_<categoria>` está
 * marcado. Preferências ainda não carregadas contam como desabilitadas: é
 * preferível perder um alerta a exibir algo que o usuário desligou.
 */
export const isPushNotificationEnabled = (store, flag) => {
  if (!flag) return false;

  const flags = getPushFlags(store);
  if (!Array.isArray(flags)) return false;

  return flags.includes(`push_${flag}`);
};

export const hasAnyPushNotificationEnabled = store => {
  const flags = getPushFlags(store);
  return Array.isArray(flags) && flags.length > 0;
};

/**
 * Filtro por canal (seção "Caixas de entrada" das preferências). Enquanto a
 * lista não chega do servidor não bloqueamos por aqui — as outras validações
 * seguem ativas —, mas assim que ela chega o canal precisa estar explicitamente
 * habilitado.
 */
export const isInboxNotificationEnabled = (store, inboxId) => {
  const uiFlags = store?.getters?.['inboxNotificationPreferences/getUIFlags'];
  if (!uiFlags?.isFetched) return true;

  const records =
    store?.getters?.['inboxNotificationPreferences/getInboxPreferences'];
  if (!Array.isArray(records)) return true;

  const id = Number(inboxId);
  if (!id) return false;

  const record = records.find(item => Number(item.id) === id);
  return record ? record.enabled === true : false;
};

/**
 * Validação completa antes de qualquer notificação do navegador.
 */
export const canShowBrowserNotification = (store, { flag, inboxId } = {}) => {
  if (!hasBrowserNotificationPermission()) return false;
  if (isBrowserPushDisabledByUser()) return false;
  if (!isPushNotificationEnabled(store, flag)) return false;

  return isInboxNotificationEnabled(store, inboxId);
};

/**
 * Traduz uma mensagem recebida para a categoria correspondente na tela de
 * preferências. Conversas atribuídas a outro atendente não têm categoria
 * própria e, portanto, nunca geram notificação local.
 */
export const resolveNewMessagePushFlag = ({ assigneeId, currentUserId }) => {
  if (!assigneeId) return PUSH_NOTIFICATION_FLAGS.CONVERSATION_CREATION;

  if (currentUserId && Number(assigneeId) === Number(currentUserId)) {
    return PUSH_NOTIFICATION_FLAGS.ASSIGNED_CONVERSATION_NEW_MESSAGE;
  }

  return null;
};
